import { gql, useQuery } from '@apollo/client';
import { EnvelopeIcon, LinkIcon, UsersIcon } from '@heroicons/react/24/outline';
import { SyndicateTypeEnumType } from '__generated__/graphql';
import classNames from 'classnames';
import { FC } from 'react';
import { useParams } from 'react-router-dom';

const GET_SYNDICATE_TYPE_QUERY = gql(`
  query GetSyndicateTypeForInviteTabs($id: ID!) {
    syndicate(id: $id) {
      id
      type
    }
  }
`);

const DealTabsForInvites: FC<{ activeTab: string; onSelect: (tab: string) => void }> = ({
  activeTab,
  onSelect,
}) => {
  const { syndicateId } = useParams<{ syndicateId: string }>();
  const { data } = useQuery(GET_SYNDICATE_TYPE_QUERY, {
    variables: { id: syndicateId },
  });

  const isRuv = data?.syndicate?.type === SyndicateTypeEnumType.Ruv;

  const tabs = [
    {
      title: 'Manage Invites',
      url: 'manage-invites',
      description: 'Track and remind invited investors',
      icon: UsersIcon,
    },
    ...(!isRuv
      ? [
          {
            title: 'Invite your network',
            url: 'invite-network',
            description: 'Send deal invites via email',
            icon: EnvelopeIcon,
          },
        ]
      : []),
    {
      title: 'Invite Links',
      url: 'invite-links',
      description: 'Create an easily shareable link',
      icon: LinkIcon,
    },
  ];

  return (
    <nav aria-label="Invite sections">
      <ol className="flex gap-3 overflow-x-auto p-1">
        {tabs.map(tab => {
          const isActive = activeTab === tab.url;
          const Icon = tab.icon;

          return (
            <li key={tab.url} className="shrink-0">
              <button
                type="button"
                onClick={() => onSelect(tab.url)}
                aria-current={isActive ? 'page' : undefined}
                className={classNames(
                  'group flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all',
                  isActive
                    ? 'cursor-default border-indigo-500 bg-indigo-50/60 shadow-sm ring-1 ring-indigo-500/20'
                    : 'border-gray-200 bg-white hover:border-indigo-200 hover:shadow-sm'
                )}
              >
                <span
                  className={classNames(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors',
                    isActive
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-indigo-50 group-hover:text-indigo-600'
                  )}
                >
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <span>
                  <span
                    className={classNames(
                      'block text-sm font-semibold',
                      isActive ? 'text-indigo-700' : 'text-gray-800'
                    )}
                  >
                    {tab.title}
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-500">{tab.description}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default DealTabsForInvites;
